import { HostSdk, ViewMode } from '../types/host-sdk';
import { Dimensions, widgetExpandedHeight } from './dimensions';

interface Resizer {
  expand(): Promise<void>;
  collapse(withPopup?: boolean): Promise<void>;
  hide(): Promise<void>;
}

export const minimumBuffer = 150;

const getCollapsedDimension = (dimensions: Dimensions, withPopup: boolean) =>
  withPopup ? dimensions.collapsedWithPopup : dimensions.collapsed;

const hide = async (hostSdk: HostSdk) => {
  await hostSdk.resizeTo(0, 0);
};

export const getResizer = (
  withQab: boolean,
  isMobile: boolean,
  hostSdk: HostSdk,
  dimensions: Dimensions,
): Resizer => {
  if (withQab) {
    return mobileQabResizer(hostSdk);
  }
  if (isMobile && hostSdk.getViewMode() === ViewMode.Editor) {
    return mobileEditorResizer(hostSdk, dimensions);
  }
  if (isMobile) {
    return mobileNonQabResizer(hostSdk, dimensions);
  }
  return desktopResizer(hostSdk, dimensions);
};

const mobileQabResizer = (hostSdk: HostSdk): Resizer => ({
  async expand() {
    hostSdk.showFullscreen();
  },
  async collapse() {
    hostSdk.hideFullscreen();
  },
  async hide() {
    hide(hostSdk);
  },
});

const mobileNonQabResizer = (
  hostSdk: HostSdk,
  dimensions: Dimensions,
): Resizer => ({
  async expand() {
    await hostSdk.resizeTo('100%', '100%');
    hostSdk.showFullscreen();
  },
  async collapse(withPopup: boolean = false) {
    const dimension = getCollapsedDimension(dimensions, withPopup);
    await hostSdk.resizeTo(dimension.width, dimension.height);
    hostSdk.hideFullscreen();
  },
  async hide() {
    hide(hostSdk);
  },
});

const mobileEditorResizer = (
  hostSdk: HostSdk,
  dimensions: Dimensions,
): Resizer => {
  return {
    async expand() {
      const { width, height } = await hostSdk.getScreenSize();
      await hostSdk.resizeTo(width, height);
    },
    async collapse(withPopup: boolean = false) {
      const dimension = getCollapsedDimension(dimensions, withPopup);
      await hostSdk.resizeTo(dimension.width, dimension.height);
    },
    async hide() {
      await hostSdk.resizeTo(0, 0);
    },
  };
};

const desktopResizer = (hostSdk: HostSdk, dimensions: Dimensions): Resizer => {
  let resizeQueue = Promise.resolve();

  const enqueueResize = (resizeAction: () => Promise<void>) => {
    resizeQueue = resizeQueue.then(resizeAction).catch((err) => {
      console.error('Resize action failed:', err);
    });
  };

  return {
    async expand() {
      enqueueResize(async () => {
        const hostScreenSize = await hostSdk.getScreenSize();

        const size =
          hostScreenSize.height < widgetExpandedHeight
            ? hostScreenSize
            : hostScreenSize.height >=
              dimensions.expanded.height + minimumBuffer
            ? dimensions.expanded
            : dimensions.shortExpanded;

        await hostSdk.resizeTo(size.width, size.height);
      });
    },
    async collapse(withPopup: boolean = false) {
      enqueueResize(async () => {
        const dimension = getCollapsedDimension(dimensions, withPopup);
        await hostSdk.resizeTo(dimension.width, dimension.height);
      });
    },
    async hide() {
      enqueueResize(async () => {
        hide(hostSdk);
      });
    },
  };
};
